import React from 'react';
import { ModelResultType } from '../IGPRScore.interface';
import styles from '../IGPRScore.module.scss';
import { transformText } from 'app/utils/functions';

interface ModelResultProps {
  hidden: boolean;
  modelResult: ModelResultType;
}

export const ModelResult: React.FC<ModelResultProps> = ({
  hidden,
  modelResult
}) => {
  const groupReasoning = (
    reasoning: Array<[string, number]>
  ): Record<string, string[]> => {
    const grouped: Record<string, string[]> = {};
    for (const [compoundKey, val] of reasoning) {
      const [baseKey, ...rest] = compoundKey.split('_');
      const numbers = rest.join(', ');
      if (!grouped[baseKey]) {
        grouped[baseKey] = [];
      }
      grouped[baseKey].push(`${numbers}: ${val.toFixed(4)}`);
    }
    return grouped;
  };

  const buildReasoningItems = (
    grouped: Record<string, string[]>
  ): JSX.Element[] => {
    const items: JSX.Element[] = [];
    for (const [baseKey, entries] of Object.entries(grouped)) {
      for (const entry of entries) {
        const [numbers, val] = entry.split(': ');
        const formattedNumbers = numbers
          .split(', ')
          .map((num) =>
            num.trim().replaceAll('medication', '').replaceAll('problem', '')
          )
          .filter(Boolean)
          .join(',');
        items.push(
          <li key={`${baseKey}_${numbers}`} className={styles.reasoningItem}>
            <strong>
              {transformText(baseKey)} {formattedNumbers}:
            </strong>{' '}
            {val}
          </li>
        );
      }
    }
    return items;
  };
  const medications = groupReasoning(
    Object.entries(modelResult.reasoning ?? {}).filter(([key]) =>
      key.startsWith('medication_')
    )
  );

  const problems = groupReasoning(
    Object.entries(modelResult.reasoning ?? {}).filter(([key]) =>
      key.startsWith('problem_')
    )
  );

  const otherReasoning = Object.entries(modelResult.reasoning ?? {}).filter(
    ([key]) => !key.startsWith('medication_') && !key.startsWith('problem_')
  );

  return (
    <section
      id="result-ai-model"
      className={`mt-8 ml-8 mr-8 ${styles.resutAiModel}`}
    >
      <div className={styles.animated} hidden={hidden} aria-hidden={hidden}>
        <div className={styles.part}>
          <div className={styles.partFieldset}>
            <h2 className={styles.resultAiModelFieldsetH2}>Result</h2>
            <p className="body-sm-r">
              The current float result value is:{' '}
              <strong>{modelResult.prediction?.toString()}</strong>. This should
              be interpreted.
            </p>
          </div>
        </div>
        <div className={styles.part}>
          <div className={styles.partFieldset}>
            <h2 className={styles.resultAiModelFieldsetH2}>Reasoning</h2>
            <p>{modelResult.recommendation}</p>
            {modelResult.reasoning && (
              <>
                <ul className={styles.reasoningList}>
                  {otherReasoning.map(([key, value]) => (
                    <li key={key} className={styles.reasoningItem}>
                      <strong>{transformText(key)}:</strong> {value.toFixed(4)}
                    </li>
                  ))}
                </ul>
                {Object.keys(medications).length > 0 && (
                  <>
                    <h3 className={styles.resultAiModelFieldsetH3}>
                      Medications
                    </h3>
                    <ul className={styles.reasoningList}>
                      {buildReasoningItems(medications)}
                    </ul>
                  </>
                )}
                {Object.keys(problems).length > 0 && (
                  <>
                    <h3 className={styles.resultAiModelFieldsetH3}>Problems</h3>
                    <ul className={styles.reasoningList}>
                      {buildReasoningItems(problems)}
                    </ul>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModelResult;
